import type { RefObject } from "react";
import type {
  AssistantMessage,
  ChatMessage,
  MarkdownData,
} from "duck/ui/chat/types";
import { ChatMessageRole } from "duck/ui/chat/types";
import {
  extractMessageContents,
  isRenderableMessageType,
} from "duck/ui/chat/utils";
import type { TFunction } from "i18next";

import type { ChatEvent, Message, SSEController } from "./types";
import { hasMessageContent } from "./utils";

/**
 * Helper function to update a specific message in the messages array
 */
const updateMessage = (
  messages: ChatMessage[],
  id: string,
  updater: (msg: ChatMessage) => ChatMessage
): ChatMessage[] => {
  const idx = messages.findIndex((m) => m.id === id);
  if (idx === -1) {
    console.warn(`The message with id {id} could not be found`);

    return messages;
  }

  const updated = [...messages];
  updated[idx] = updater(updated[idx]);

  return updated;
};

/**
 * Apply protocol output update to messages array
 * TODO: ideally we can avoid creating a placeholder assistant
 * message so that we can show backQuack (status_message_update for example).
 * - this way we could possibly avoid the complex logic in here and just render
 *   every message in the order it appears
 */
export const applyProtocolUpdate = (
  messages: ChatMessage[],
  evt: ChatEvent & { type: "update" },
  assistantMessageId: string
): ChatMessage[] => {
  // separate AI messages from other renderable message types
  // - only messages with types defined in ChatMessageRole are renderable
  const { aiMessages, renderableMessages } = evt.protocolOutput.messages.reduce(
    (acc, msg) => {
      if (!isRenderableMessageType(msg.type)) {
        // silently ignore non-renderable message types
        return acc;
      }

      if (msg.type === ChatMessageRole.AI) {
        acc.aiMessages.push(msg);
      } else {
        acc.renderableMessages.push(msg);
      }

      return acc;
    },
    { aiMessages: [] as Message[], renderableMessages: [] as Message[] }
  );

  // build AssistantMessage state from all AI message content
  const state: AssistantMessage = {
    messages: aiMessages.flatMap(extractMessageContents).filter(Boolean),
  };

  // extract markdown content for the content field (backwards compatibility)
  const markdownContent =
    (state.messages?.[0] as MarkdownData)?.markdown_message || "";

  let updated = [...messages];

  // find the pre-created assistant message index
  // (so we can stream into that)
  const aiMessageIdx = updated.findIndex((m) => m.id === assistantMessageId);

  // update the pre-created assistant message with aggregated AI content
  if (aiMessageIdx !== -1) {
    updated[aiMessageIdx] = {
      ...updated[aiMessageIdx],
      // Adopt the server's real message id so feedback targets a known message.
      // Captured once and never reassigned: this id also keys the rendered bubble,
      // and a turn can stream as several AI messages, so advancing it would remount
      // the bubble mid-stream. Freezing the first captured id keeps the key stable.
      // For the rare multi-AI-message turn that means feedback targets the first
      // captured id rather than the last; that's fine — the server accepts any
      // in-turn id, and the merged bubble re-splits into per-message bubbles on the
      // post-stream refetch anyway.
      serverId: updated[aiMessageIdx].serverId ?? aiMessages.at(-1)?.id,
      state,
      bytes: Math.max(updated[aiMessageIdx].bytes || 0, evt.bytes),
      backQuack: evt.protocolOutput.back_quack,
      content: markdownContent,
    };
  }

  // process renderable message types (human, tool_call) as individual messages
  // track insertion index so multiple new messages maintain their order
  let insertIdx = aiMessageIdx;

  renderableMessages.forEach((rawMsg) => {
    const existingIdx = updated.findIndex((m) => m.id === rawMsg.id);

    const message: ChatMessage = {
      id: rawMsg.id,
      role: rawMsg.type as any,
      content: rawMsg.content,
      metadata: rawMsg.metadata,
      safeMode: rawMsg.metadata?.safe_mode === true,
    };

    if (existingIdx !== -1) {
      // update existing message
      updated[existingIdx] = message;
    } else {
      // insert new message before the assistant message (so tool calls appear before the response)
      if (insertIdx !== -1) {
        updated.splice(insertIdx, 0, message);
        insertIdx++; // increment so next new message goes after this one
      } else {
        // no AI message yet, append to end
        updated.push(message);
      }
    }
  });

  return updated;
};

/**
 * Creates a chat event handler for processing SSE events during message streaming.
 *
 * This handler manages the lifecycle of assistant messages during streaming:
 * - Updates message content as it streams in
 * - Handles streaming completion and errors
 * - Manages streaming state transitions
 *
 * @param assistantMessageId - ID of the assistant message being streamed
 * @param setMessages - Callback to update the messages array
 * @param setIsStreaming - Callback to update streaming state
 * @param t - Translation function for the user-visible fallback message content
 * @returns Event handler function for SSE events
 */
interface ChatEventHandlerProps {
  assistantMessageId: string;
  setMessages: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  setIsStreaming: (streaming: boolean) => void;
  onStreamingEnd: () => Promise<void>;
  t: TFunction;
}

export const createChatEventHandler =
  ({
    assistantMessageId,
    setMessages,
    setIsStreaming,
    onStreamingEnd,
    t,
  }: ChatEventHandlerProps) =>
  (evt: ChatEvent) => {
    switch (evt.type) {
      case "update": {
        setMessages((prev) =>
          applyProtocolUpdate(prev, evt, assistantMessageId)
        );
        break;
      }

      case "end":
        setMessages((prev) =>
          updateMessage(prev, assistantMessageId, (msg) => ({
            ...msg,
            endedAt: Date.now(),
          }))
        );
        setIsStreaming(false);
        onStreamingEnd().catch((err) =>
          console.error("onStreamingEnd failed:", err)
        );
        break;

      case "error":
        console.error("Chat SSE error:", evt.error);
        setMessages((prev) =>
          updateMessage(prev, assistantMessageId, (msg) => {
            const hasContent = hasMessageContent(msg.content);

            return {
              ...msg,
              endedAt: Date.now(),
              aborted: !hasContent,
              content: hasContent ? msg.content : t("duck:message_error"),
            };
          })
        );
        setIsStreaming(false);
        onStreamingEnd().catch((err) =>
          console.error("onStreamingEnd failed:", err)
        );
        break;
    }
  };

/**
 * Creates a stop streaming handler for aborting ongoing message streaming.
 *
 * This handler manages the cleanup when streaming is manually stopped:
 * - Aborts the active SSE connection
 * - Marks the last AI message as aborted if it has no content
 * - Updates streaming state
 *
 * @param setMessages - Callback to update the messages array
 * @param setIsStreaming - Callback to update streaming state
 * @param activeController - Ref to the active SSE controller
 * @param t - Translation function for the user-visible fallback message content
 * @returns Handler function for stopping streaming
 */
interface StopStreamingProps {
  setMessages: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  setIsStreaming: (streaming: boolean) => void;
  activeController: RefObject<SSEController | null>;
  onStreamingEnd: () => Promise<void>;
  t: TFunction;
}

export const createStopStreamingHandler =
  ({
    setMessages,
    setIsStreaming,
    activeController,
    onStreamingEnd,
    t,
  }: StopStreamingProps) =>
  () => {
    // Abort SSE
    activeController.current?.abort();
    activeController.current = null;

    setIsStreaming(false);
    onStreamingEnd().catch((err) =>
      console.error("onStreamingEnd failed:", err)
    );

    // Mark last unfinished AI message
    setMessages((prev) => {
      const updated = [...prev];
      const idx = updated.findLastIndex(
        (m) => m.role === ChatMessageRole.AI && !m.endedAt
      );
      if (idx !== -1) {
        const msg = updated[idx];
        const hasContent = hasMessageContent(msg.content);
        updated[idx] = {
          ...msg,
          endedAt: Date.now(),
          aborted: !hasContent,
          content: hasContent ? msg.content : t("duck:message_stopped"),
        };
      }

      return updated;
    });
  };
